import React from 'react';
import ReactMarkdown from 'react-markdown';
import styles from './RecipeCleanupPreview.module.css';

interface RecipeCleanupPreviewProps {
  originalMarkdown: string;
  cleanedMarkdown: string;
  onAccept: () => void;
  onReject: () => void;
  isLoading: boolean;
  error?: string | null;
}

const RecipeCleanupPreview: React.FC<RecipeCleanupPreviewProps> = ({
  originalMarkdown,
  cleanedMarkdown,
  onAccept,
  onReject,
  isLoading,
  error,
}) => {
  if (isLoading) {
    return <div className="text-center p-5 text-lg text-slate-700 dark:text-slate-300">Cleaning up recipe... Please wait.</div>;
  }

  if (error) {
    return (
      <div className="text-center p-5">
        <p className="text-red-600 dark:text-red-400 mb-4 text-lg">Error: {error}</p>
        {/* Using existing button style for consistency with action buttons for now */}
        <button onClick={onReject} className={`${styles.button} ${styles.rejectButton}`}>
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 md:p-6 mt-5 shadow-lg">
      <h3 className="text-center text-xl md:text-2xl font-semibold text-slate-800 dark:text-slate-100 mb-4 md:mb-6">Recipe Cleanup Preview</h3>
      <div className="flex flex-col md:flex-row justify-between gap-4 md:gap-6 mb-4 md:mb-6">
        <div className="flex-1 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-md p-3 md:p-4 min-w-0 shadow-sm">
          <h4 className="text-lg md:text-xl font-medium text-slate-700 dark:text-slate-200 mt-0 mb-3 pb-2 border-b border-slate-200 dark:border-slate-600">Before</h4>
          <div className="prose dark:prose-invert max-w-none overflow-y-auto max-h-[300px] md:max-h-[400px] p-1 rounded bg-white dark:bg-slate-900 text-sm md:text-base">
            <ReactMarkdown>{originalMarkdown}</ReactMarkdown>
          </div>
        </div>
        <div className="flex-1 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-md p-3 md:p-4 min-w-0 shadow-sm">
          <h4 className="text-lg md:text-xl font-medium text-slate-700 dark:text-slate-200 mt-0 mb-3 pb-2 border-b border-slate-200 dark:border-slate-600">After (Suggested Changes)</h4>
          <div className="prose dark:prose-invert max-w-none overflow-y-auto max-h-[300px] md:max-h-[400px] p-1 rounded bg-white dark:bg-slate-900 text-sm md:text-base">
            <ReactMarkdown>{cleanedMarkdown}</ReactMarkdown>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4 md:mt-6">
        <button onClick={onAccept} className={`${styles.button} ${styles.acceptButton} w-full sm:w-auto`}>
          Accept Changes
        </button>
        <button onClick={onReject} className={`${styles.button} ${styles.rejectButton} w-full sm:w-auto`}>
          Reject Changes
        </button>
      </div>
    </div>
  );
};

export default RecipeCleanupPreview;